import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, FileSpreadsheet, Upload, XCircle } from "lucide-react";
import { useCallback, useState } from "react";
import { Navigate } from "react-router-dom";

import { DalGestionaleNota } from "@/components/dashboard/DalGestionaleNota";
import { PageContainer } from "@/components/PageContainer";
import { useTipiDalGestionale } from "@/hooks/useDashboard";
import { useMe } from "@/hooks/useMe";
import { ApiError, apiFetch, apiUploadFile } from "@/lib/api";
import { isMaitimeStaff } from "@/lib/nav-config";
import { cn } from "@/lib/utils";
import type { M1Snapshot, M1TipoFile } from "@/types/api";

interface TipoConfig {
  slug: M1TipoFile;
  label: string;
  hint: string;
  fileEsempio: string;
}

const TIPI: TipoConfig[] = [
  {
    slug: "ordini",
    label: "Ordini clienti",
    hint: "quotidiano",
    fileEsempio: "ordini clienti.xlsx",
  },
  {
    slug: "giacenze_pf",
    label: "Giacenze PF — E80 (BC 700)",
    hint: "quotidiano (anche più volte)",
    fileEsempio: "E80.xls",
  },
  {
    slug: "giacenze_pf_terra",
    label: "Giacenze PF — a terra (BC 710)",
    hint: "quotidiano",
    fileEsempio: "PFTERRA.xls",
  },
  {
    slug: "piano_produzione",
    label: "Piano produzione",
    hint: "quotidiano",
    fileEsempio: "PIANO PRODUZIONE.xlsx",
  },
  {
    slug: "anagrafica_articoli",
    label: "Anagrafica articoli",
    hint: "settimanale / on-change",
    fileEsempio: "anagrafica articoli.xls",
  },
  {
    slug: "anagrafica_clienti",
    label: "Anagrafica clienti",
    hint: "settimanale / on-change",
    fileEsempio: "ANAGRAFICA CLIENTI.xlsx",
  },
];

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function M1UploadPage() {
  const { data: me, isLoading } = useMe();
  const dalGestionale = useTipiDalGestionale();

  const { data: snapshots = [] } = useQuery({
    queryKey: ["m1", "snapshots"],
    queryFn: () => apiFetch<M1Snapshot[]>("/api/v1/m1/snapshot?limit=50"),
    enabled: !!me && isMaitimeStaff(me),
  });

  if (!isLoading && me && !isMaitimeStaff(me)) {
    return <Navigate to="/" replace />;
  }

  // Indicizza per tipo l'ultimo snapshot in stato "committato".
  const ultimoCommittatoPerTipo = snapshots.reduce<Record<string, M1Snapshot>>((acc, s) => {
    if (s.stato !== "committato") return acc;
    if (!acc[s.tipo_file] || acc[s.tipo_file].caricato_il < s.caricato_il) {
      acc[s.tipo_file] = s;
    }
    return acc;
  }, {});

  return (
    <PageContainer>
      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          Backoffice Maitime · M1 Cruscotto Ordini
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">Upload dati operativi</h1>
        <p className="text-sm text-muted-foreground max-w-2xl pt-1">
          Trascina i file Excel forniti da Mister Pet nelle aree corrispondenti. Ogni upload viene
          parsato, storicizzato e committato nel modulo M1. Le anagrafiche fanno upsert.
        </p>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DalGestionaleNota tipi={TIPI.filter((t) => dalGestionale.has(t.slug))} />
        {TIPI.filter((t) => !dalGestionale.has(t.slug)).map((tipo) => (
          <Dropzone
            key={tipo.slug}
            tipo={tipo}
            ultimoSnapshot={ultimoCommittatoPerTipo[tipo.slug]}
          />
        ))}
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-medium text-foreground">Snapshot recenti</h2>
        {snapshots.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nessun upload ancora effettuato.</p>
        ) : (
          <div className="rounded-md border bg-card/40 divide-y">
            {snapshots.slice(0, 12).map((s) => (
              <div key={s.id} className="px-4 py-3 grid grid-cols-12 gap-3 items-center text-sm">
                <span className="col-span-3 font-medium text-foreground">{s.tipo_file}</span>
                <span className="col-span-3 text-muted-foreground truncate">{s.filename_originale}</span>
                <span className="col-span-2 text-xs text-muted-foreground">{formatDateTime(s.caricato_il)}</span>
                <span className="col-span-2 text-xs text-emerald-700">ok {s.n_righe_ok}</span>
                <span className="col-span-1 text-xs text-amber-700">
                  {s.n_righe_err > 0 ? `err ${s.n_righe_err}` : ""}
                </span>
                <span
                  className={cn(
                    "col-span-1 text-xs",
                    s.stato === "committato" && "text-emerald-700",
                    s.stato === "sostituito" && "text-muted-foreground",
                    s.stato === "errore" && "text-red-700"
                  )}
                >
                  {s.stato}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </PageContainer>
  );
}

interface DropzoneProps {
  tipo: TipoConfig;
  ultimoSnapshot: M1Snapshot | undefined;
}

function Dropzone({ tipo, ultimoSnapshot }: DropzoneProps) {
  const queryClient = useQueryClient();
  const [isDragging, setIsDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<M1Snapshot | null>(null);

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("tipo", tipo.slug);
      fd.append("file", file);
      return apiUploadFile<M1Snapshot>("/api/v1/dati/upload", fd);
    },
    onSuccess: (snap) => {
      setLastResult(snap);
      setErrorMessage(null);
      queryClient.invalidateQueries({ queryKey: ["m1"] });
    },
    onError: (err) => {
      setErrorMessage(err instanceof ApiError ? err.message : "errore upload");
    },
  });

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const f = files[0];
      mutation.mutate(f);
    },
    [mutation]
  );

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        handleFiles(e.dataTransfer.files);
      }}
      className={cn(
        "rounded-lg border bg-card/40 p-5 space-y-3 transition-colors",
        isDragging && "border-brand-700 bg-brand-50/40",
        mutation.isPending && "opacity-60"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium text-foreground">{tipo.label}</h3>
          <p className="text-xs text-muted-foreground">{tipo.hint}</p>
        </div>
        <FileSpreadsheet className="size-4 text-muted-foreground shrink-0" />
      </div>

      <label
        className={cn(
          "block rounded-md border border-dashed px-4 py-6 text-center cursor-pointer hover:bg-accent/50",
          isDragging && "border-brand-700 bg-brand-50/60"
        )}
      >
        <input
          type="file"
          accept=".xls,.xlsx"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          disabled={mutation.isPending}
        />
        <Upload className="mx-auto size-4 text-muted-foreground mb-2" />
        <p className="text-xs text-muted-foreground">
          {mutation.isPending ? "Caricamento…" : "Trascina o clicca per scegliere"}
        </p>
        <p className="text-[11px] text-muted-foreground/60 mt-1">{tipo.fileEsempio}</p>
      </label>

      {lastResult && (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900 flex items-center gap-2">
          <CheckCircle2 className="size-3.5 shrink-0" />
          <span>
            {lastResult.n_righe_ok} righe ok
            {lastResult.n_righe_err > 0 && `, ${lastResult.n_righe_err} errori`} ·{" "}
            {formatBytes(lastResult.dimensione_bytes)}
          </span>
        </div>
      )}

      {errorMessage && (
        <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-900 flex items-center gap-2">
          <XCircle className="size-3.5 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      {!lastResult && !errorMessage && ultimoSnapshot && (
        <div className="text-xs text-muted-foreground">
          Ultimo: {formatDateTime(ultimoSnapshot.caricato_il)} · {ultimoSnapshot.n_righe_ok} righe
        </div>
      )}
    </div>
  );
}
